/**
 * CareLogsList Component
 *
 * Displays a timeline view of care logs grouped by date, newest first.
 * Supports infinite scroll / "load more" functionality.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { CareLogCard, CareLogSummary } from './CareLogCard';
import { FileText, ChevronDown } from 'lucide-react';

interface ListCareLogsResponse {
  success: boolean;
  careLogs: CareLogSummary[];
  totalCount: number;
  lastDocumentId?: string;
  error?: string;
}

interface CareLogsListProps {
  caseFileId: string;
  startDate?: string;
  endDate?: string;
  tags?: string[];
  onLogClick?: (logId: string) => void;
  onEditClick?: (logId: string) => void;
  refreshKey?: number;
}

interface GroupedLogs {
  date: string;
  dateLabel: string;
  logs: CareLogSummary[];
}

function groupLogsByDate(logs: CareLogSummary[]): GroupedLogs[] {
  const groups: Map<string, CareLogSummary[]> = new Map();

  logs.forEach((log) => {
    const date = log.logDate;
    if (!groups.has(date)) {
      groups.set(date, []);
    }
    groups.get(date)!.push(log);
  });

  // Sort dates descending (newest first)
  const sortedDates = Array.from(groups.keys()).sort((a, b) => b.localeCompare(a));

  return sortedDates.map((date) => {
    const dateObj = new Date(date + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let dateLabel: string;
    if (dateObj.getTime() === today.getTime()) {
      dateLabel = 'Today';
    } else if (dateObj.getTime() === yesterday.getTime()) {
      dateLabel = 'Yesterday';
    } else {
      dateLabel = dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: dateObj.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      });
    }

    return {
      date,
      dateLabel,
      logs: groups.get(date)!,
    };
  });
}

export function CareLogsList({
  caseFileId,
  startDate,
  endDate,
  tags,
  onLogClick,
  onEditClick,
  refreshKey = 0,
}: CareLogsListProps): JSX.Element {
  const [logs, setLogs] = useState<CareLogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastDocumentId, setLastDocumentId] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const fetchLogs = useCallback(async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setLogs([]);
      setLastDocumentId(undefined);
    }
    setError(null);

    try {
      const listCareLogs = httpsCallable<
        {
          caseFileId: string;
          startDate?: string;
          endDate?: string;
          tags?: string[];
          limit?: number;
          lastDocumentId?: string;
        },
        ListCareLogsResponse
      >(functions, 'listCareLogs');

      const result = await listCareLogs({
        caseFileId,
        startDate,
        endDate,
        tags: tags && tags.length > 0 ? tags : undefined,
        limit: 20,
        lastDocumentId: isLoadMore ? lastDocumentId : undefined,
      });

      if (result.data.success) {
        const newLogs = result.data.careLogs;
        setLogs((prev) => (isLoadMore ? [...prev, ...newLogs] : newLogs));
        setLastDocumentId(result.data.lastDocumentId);
        setTotalCount(result.data.totalCount);
        setHasMore(newLogs.length === 20 && result.data.lastDocumentId !== undefined);
      } else {
        setError(result.data.error || 'Failed to load care logs');
      }
    } catch (err) {
      console.error('Error fetching care logs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load care logs');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [caseFileId, startDate, endDate, tags, lastDocumentId]);

  useEffect(() => {
    fetchLogs(false);
  }, [caseFileId, startDate, endDate, tags, refreshKey]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchLogs(true);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card">
            <div className="card-body">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-4 w-24 bg-soft-neutral rounded animate-pulse" />
                <div className="h-4 w-16 bg-soft-neutral rounded animate-pulse" />
              </div>
              <div className="h-20 bg-soft-neutral rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="alert alert-error flex justify-between items-center">
        <span>Error: {error}</span>
        <button onClick={() => fetchLogs(false)} className="btn-ghost btn-sm">
          Retry
        </button>
      </div>
    );
  }

  // Empty state
  if (logs.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-12">
          <FileText size={48} className="mx-auto text-muted mb-4" />
          <h3 className="text-lg font-semibold text-ink mb-2">No care logs yet</h3>
          <p className="text-muted mb-4">
            Start recording daily observations, conversations, and care activities.
          </p>
        </div>
      </div>
    );
  }

  const groupedLogs = groupLogsByDate(logs);

  return (
    <div className="space-y-6">
      {/* Total count */}
      <div className="text-sm text-muted">
        {totalCount} {totalCount === 1 ? 'entry' : 'entries'} total
      </div>

      {/* Timeline view grouped by date */}
      {groupedLogs.map((group) => (
        <div key={group.date}>
          {/* Date header */}
          <div className="flex items-center gap-3 mb-3">
            <h3 className="text-sm font-semibold text-muted uppercase tracking-wide">
              {group.dateLabel}
            </h3>
            <div className="flex-1 h-px bg-soft-neutral" />
            <span className="text-xs text-muted">
              {group.logs.length} {group.logs.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>

          {/* Logs for this date */}
          <div className="space-y-3">
            {group.logs.map((log) => (
              <CareLogCard
                key={log.logId}
                careLog={log}
                onClick={onLogClick ? () => onLogClick(log.logId) : undefined}
                onEdit={onEditClick}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Load more button */}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="btn-secondary flex items-center gap-2"
          >
            {loadingMore ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-evergreen border-t-transparent rounded-full" />
                Loading...
              </>
            ) : (
              <>
                <ChevronDown size={16} />
                Load More
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default CareLogsList;
